<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import AddAttribute from 'dashboard/routes/dashboard/settings/attributes/AddAttribute.vue';
import EditAttribute from 'dashboard/routes/dashboard/settings/attributes/EditAttribute.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AttributeListItem from 'dashboard/components-next/ConversationWorkflow/AttributeListItem.vue';
import ContactSyncSettings from './ContactSyncSettings.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const searchQuery = ref('');
const selectedAttribute = ref({});
const [showAddPopup, toggleAddPopup] = useToggle(false);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);

const uiFlags = computed(() => getters['attributes/getUIFlags'].value);
const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value('contact_attribute')
);

const filteredAttributes = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return attributes.value;

  return attributes.value.filter(attribute =>
    [
      attribute.attribute_display_name,
      attribute.attribute_key,
      attribute.attribute_description,
    ].some(value => value?.toLocaleLowerCase().includes(query))
  );
});

const displayedAttributes = computed(() =>
  filteredAttributes.value.map(attribute => ({
    ...attribute,
    label: attribute.attribute_display_name,
    type: attribute.attribute_display_type,
    value: attribute.attribute_key,
  }))
);

const closeAdd = () => toggleAddPopup(false);
const closeEdit = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};
const editAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleEditPopup(true);
};
const deleteAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleDeletePopup(true);
};

const confirmDelete = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('CONTACT_FIELDS_SETTINGS.DELETE_SUCCESS'));
    closeDelete();
  } catch (error) {
    useAlert(
      error?.response?.message || t('CONTACT_FIELDS_SETTINGS.DELETE_ERROR')
    );
  }
};

onMounted(() => store.dispatch('attributes/get'));
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CONTACT_FIELDS_SETTINGS.LOADING')"
  >
    <template #header>
      <div class="w-full max-w-6xl mx-auto">
        <BaseSettingsHeader
          v-model:search-query="searchQuery"
          :title="$t('CONTACT_FIELDS_SETTINGS.TITLE')"
          :description="$t('CONTACT_FIELDS_SETTINGS.DESCRIPTION')"
          :search-placeholder="$t('CONTACT_FIELDS_SETTINGS.SEARCH_PLACEHOLDER')"
        >
          <template #actions>
            <Button
              :label="$t('CONTACT_FIELDS_SETTINGS.ADD')"
              icon="i-lucide-plus"
              size="sm"
              @click="toggleAddPopup(true)"
            />
          </template>
        </BaseSettingsHeader>
      </div>
    </template>

    <template #body>
      <div class="w-full max-w-6xl mx-auto">
        <ContactSyncSettings
          :custom-fields="attributes"
          @create-contact-field="toggleAddPopup(true)"
        />
        <section
          v-if="displayedAttributes.length"
          class="overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <header
            class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-n-weak bg-n-alpha-black1"
          >
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CONTACT_FIELDS.CONFIGURED') }}
              </h2>
              <p class="mt-0.5 text-xs text-n-slate-11">
                {{ $t('CONTACT_FIELDS.DESCRIPTION') }}
              </p>
            </div>
            <span class="text-xs font-medium text-n-slate-11">
              {{
                $t('CONTACT_FIELDS.COUNT', {
                  count: displayedAttributes.length,
                })
              }}
            </span>
          </header>
          <div class="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
            <AttributeListItem
              v-for="attribute in displayedAttributes"
              :key="attribute.id"
              class="!p-3 !py-3 rounded-lg outline outline-1 outline-n-weak"
              :attribute="attribute"
              @edit="editAttribute"
              @delete="deleteAttribute"
            />
          </div>
        </section>
        <p
          v-else
          class="flex items-center justify-center py-20 text-base text-n-slate-11"
        >
          {{ $t('CONTACT_FIELDS_SETTINGS.EMPTY') }}
        </p>
      </div>
    </template>
  </SettingsLayout>

  <AddAttribute
    v-if="showAddPopup"
    :on-close="closeAdd"
    :selected-attribute-model-tab="1"
    attribute-model-locked
  />
  <woot-modal v-model:show="showEditPopup" :on-close="closeEdit">
    <EditAttribute
      :selected-attribute="selectedAttribute"
      :is-updating="uiFlags.isUpdating"
      @on-close="closeEdit"
    />
  </woot-modal>
  <woot-confirm-delete-modal
    v-if="showDeletePopup"
    v-model:show="showDeletePopup"
    :title="
      $t('CONTACT_FIELDS_SETTINGS.DELETE_TITLE', {
        name: selectedAttribute.attribute_display_name,
      })
    "
    :message="$t('CONTACT_FIELDS_SETTINGS.DELETE_MESSAGE')"
    :confirm-text="$t('CONTACT_FIELDS_SETTINGS.DELETE_CONFIRM')"
    :reject-text="$t('CONTACT_FIELDS_SETTINGS.CANCEL')"
    :confirm-value="selectedAttribute.attribute_display_name"
    :confirm-place-holder-text="
      $t('CONTACT_FIELDS_SETTINGS.DELETE_PLACEHOLDER', {
        name: selectedAttribute.attribute_display_name,
      })
    "
    @on-confirm="confirmDelete"
    @on-close="closeDelete"
  />
</template>
